import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTable from "../../../../../../app/CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../../app/CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../../app/CustomHeadlessTable/components/CustomInputComponent";
import {
  addPackagingUsage34_1bLCA,
  deletePackagingUsage34_1bLCA,
  getAllPackagingUsage34_1bLCA,
  updatePackagingUsage34_1bLCA,
} from "../../../../../../apis/Product/CustomManufacturingFlow34Api";
import { getProductPackagingUsage34_1bDropdown } from "../../../../../../apis/Dropdown";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  {
    name: "Eutrophication, marine",
    unit: "kg N-eq",
    key: "eutrophication_marine",
  },
  {
    name: "Human toxicity, cancer effects",
    unit: "CTUh",
    key: "carcinogenics",
  },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  {
    name: "Particulate matter",
    unit: "disease incidence",
    key: "respiratory_effects",
  },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  {
    name: "Resource use, energy carriers",
    unit: "MJ",
    key: "fossil_fuel_depletion",
  },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const PackagingUsage34_1bTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] =
    useState(0);
  const [materialDropdown, setMaterialDropdown] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  // Fetch packaging material dropdown (Flow 34.1b)
  useEffect(() => {
    getProductPackagingUsage34_1bDropdown({
      setDropdown: setMaterialDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllPackagingUsage34_1bLCA({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && materialDropdown.length > 0) {
      const newUnits = {};
      data.forEach((row) => {
        const materialCode =
          typeof row.material === "string"
            ? row.material
            : row.material?.value || row.material?.code;
        if (materialCode) {
          const selected = materialDropdown.find(
            (opt) =>
              !opt.isCategory &&
              (opt.code === materialCode || opt.value === materialCode)
          );
          if (selected) {
            newUnits[row.id] = selected.unit || "xx";
          }
        }
      });
      setUnitMaterials(newUnits);
    }
  }, [data, materialDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      material: {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          _rowData,
          rowId = "new"
        ) => {
          const selected = dropdownData?.find(
            (opt) => opt?.value === selectedValue
          );
          if (selected) {
            setUnitMaterials((prev) => ({
              ...prev,
              [rowId]: selected.unit || "xx",
            }));
          }
        },
        dropdownDataKey: "material",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const materialCode =
        typeof editedItem.material === "string"
          ? editedItem.material
          : editedItem.material?.value || editedItem.material?.code || null;
      updatePackagingUsage34_1bLCA({
        productId,
        rowId: editedItem.id,
        material: materialCode,
        qty: editedItem.qty || 0,
        supplier: editedItem.supplier || null,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [productId, data]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const materialCode =
        typeof newItem.material === "string"
          ? newItem.material
          : newItem.material?.value || newItem.material?.code || null;
      addPackagingUsage34_1bLCA({
        productId,
        material: materialCode,
        qty: newItem.qty || 0,
        supplier: newItem.supplier || null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deletePackagingUsage34_1bLCA({
        productId,
        rowId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "material",
        accessor: "material",
        header: `${t("MATERIAL") || "Material"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_MATERIAL") || "Select material...",
        width: "90px",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "material",
            materialDropdown,
            unitMaterials,
            rowId
          ),
      },
      {
        id: "qty",
        accessor: "qty",
        header: `${t("QTY_FUNC_UNIT") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "material",
            materialDropdown,
            unitMaterials,
            rowId
          ),
      },
      {
        id: "supplier",
        accessor: "supplier",
        header: t("SUPPLIER") || "Supplier",
        type: "text",
        placeholder: t("USER_TYPES") || "User types...",
        optional: true,
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true, // read-only output
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      unitMaterials,
      materialDropdown,
      data,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: {
        label: `Total ${currentImpactCategory.name}`,
      },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      material: materialDropdown || [],
    }),
    [materialDropdown]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({ [impactCategoryAccessor]: CustomInputComponent }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default PackagingUsage34_1bTable;
